/**
 * Shared client for high-frequency, read-only tenant-management public endpoints.
 * Deduplicates in-flight requests, caches responses, and backs off on HTTP 429.
 */

export interface MaintenanceState {
  enabled: boolean
  reason?: string
  estimatedEnd?: string | null
  services?: string[]
}

export interface Announcement {
  _id: string
  tenantId: string
  title: string
  content: string
  priority: 'urgent' | 'general' | 'warning'
  target: string[]
  requireAck: boolean
  imageUrl?: string | null
  displayDurationSeconds: number | null
  repeat?: {
    enabled: boolean
    intervalMinutes: number
    durationMinutes?: number
  }
  startDate: string
  endDate: string | null
  isActive: boolean
}

const RATE_LIMIT_BACKOFF_MS = 120_000
const MAINTENANCE_CACHE_TTL_MS = 60_000
const ANNOUNCEMENT_CACHE_TTL_MS = 60_000

let maintenanceRateLimitedUntil = 0
let maintenanceInFlight: Promise<MaintenanceState> | null = null
let maintenanceCache: { key: string; at: number; state: MaintenanceState } | null = null

const announcementRateLimitedUntil = new Map<string, number>()
const announcementInFlight = new Map<string, Promise<{ success: boolean; data: Announcement[] }>>()
const announcementCache = new Map<string, { at: number; result: { success: boolean; data: Announcement[] } }>()

function buildMaintenanceKey(tenantId?: string): string {
  return tenantId ? `tenant:${tenantId}` : 'global'
}

function buildAnnouncementKey(tenantId: string, target: string, username?: string | null): string {
  const userPart = username?.trim() ? username.trim() : '_anon_'
  return `${tenantId}:${target}:${userPart}`
}

function buildTenantHeaders(tenantId?: string): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  }

  if (typeof window !== 'undefined' && window.location.hostname) {
    headers['X-Tenant-Domain'] = window.location.hostname
  }

  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId
  }

  return headers
}

async function fetchMaintenanceStatusInternal(
  apiBaseUrl: string,
  tenantId?: string
): Promise<MaintenanceState> {
  const base = apiBaseUrl.replace(/\/$/, '')
  const headers = buildTenantHeaders(tenantId)

  const globalRes = await fetch(`${base}/api/v1/tenant-management/maintenance`, {
    headers,
    credentials: 'include',
  })

  if (globalRes.status === 429) {
    maintenanceRateLimitedUntil = Date.now() + RATE_LIMIT_BACKOFF_MS
    throw new Error('maintenance_rate_limited')
  }

  const globalState: MaintenanceState = globalRes.ok
    ? await globalRes.json().catch(() => ({ enabled: false }))
    : { enabled: false }

  if (globalState.enabled) return globalState

  if (tenantId) {
    const tenantRes = await fetch(`${base}/api/v1/tenant-management/maintenance/tenant/${tenantId}`, {
      headers,
      credentials: 'include',
    })

    if (tenantRes.status === 429) {
      maintenanceRateLimitedUntil = Date.now() + RATE_LIMIT_BACKOFF_MS
      throw new Error('maintenance_rate_limited')
    }

    if (tenantRes.ok) {
      const tenantState: MaintenanceState = await tenantRes.json().catch(() => ({ enabled: false }))
      if (tenantState.enabled) return tenantState
    }
  }

  return { enabled: false }
}

export async function fetchMaintenanceStatusCached(
  apiBaseUrl: string,
  tenantId?: string
): Promise<MaintenanceState> {
  const cacheKey = buildMaintenanceKey(tenantId)
  const now = Date.now()

  if (now < maintenanceRateLimitedUntil) {
    if (maintenanceCache?.key === cacheKey) return maintenanceCache.state
    return { enabled: false }
  }

  if (maintenanceCache?.key === cacheKey && now - maintenanceCache.at < MAINTENANCE_CACHE_TTL_MS) {
    return maintenanceCache.state
  }

  if (!maintenanceInFlight) {
    maintenanceInFlight = fetchMaintenanceStatusInternal(apiBaseUrl, tenantId)
      .then((state) => {
        maintenanceCache = { key: cacheKey, at: Date.now(), state }
        return state
      })
      .catch((error) => {
        if (error instanceof Error && error.message === 'maintenance_rate_limited') {
          if (maintenanceCache?.key === cacheKey) return maintenanceCache.state
          return { enabled: false }
        }
        if (maintenanceCache?.key === cacheKey) return maintenanceCache.state
        return { enabled: false }
      })
      .finally(() => {
        maintenanceInFlight = null
      })
  }

  return maintenanceInFlight
}

export function invalidateMaintenanceCache(): void {
  maintenanceCache = null
}

async function fetchAnnouncementsInternal(
  apiBaseUrl: string,
  tenantId: string,
  target: string,
  username?: string | null
): Promise<{ success: boolean; data: Announcement[] }> {
  const base = apiBaseUrl.replace(/\/$/, '')
  let path = `/api/v1/tenant-management/announcements/active?tenantId=${encodeURIComponent(tenantId)}&target=${encodeURIComponent(target)}`
  if (username?.trim()) {
    path += `&username=${encodeURIComponent(username.trim())}`
  }

  const res = await fetch(`${base}${path}`, {
    headers: buildTenantHeaders(tenantId),
    credentials: 'include',
  })

  if (res.status === 429) {
    const cacheKey = buildAnnouncementKey(tenantId, target, username)
    announcementRateLimitedUntil.set(cacheKey, Date.now() + RATE_LIMIT_BACKOFF_MS)
    throw new Error('announcement_rate_limited')
  }

  if (!res.ok) {
    return { success: false, data: [] }
  }

  const json = await res.json().catch(() => ({ success: false, data: [] }))
  return json as { success: boolean; data: Announcement[] }
}

export async function fetchActiveAnnouncementsCached(
  apiBaseUrl: string,
  tenantId: string,
  target: string,
  username?: string | null,
  options?: { bypassCache?: boolean }
): Promise<{ success: boolean; data: Announcement[] }> {
  const cacheKey = buildAnnouncementKey(tenantId, target, username)
  const now = Date.now()
  const backoffUntil = announcementRateLimitedUntil.get(cacheKey) || 0

  if (!options?.bypassCache && now < backoffUntil) {
    const cached = announcementCache.get(cacheKey)
    if (cached) return cached.result
    return { success: false, data: [] }
  }

  if (!options?.bypassCache) {
    const cached = announcementCache.get(cacheKey)
    if (cached && now - cached.at < ANNOUNCEMENT_CACHE_TTL_MS) {
      return cached.result
    }
  }

  const existing = announcementInFlight.get(cacheKey)
  if (existing) return existing

  const request = fetchAnnouncementsInternal(apiBaseUrl, tenantId, target, username)
    .then((result) => {
      announcementCache.set(cacheKey, { at: Date.now(), result })
      return result
    })
    .catch((error) => {
      if (error instanceof Error && error.message === 'announcement_rate_limited') {
        const cached = announcementCache.get(cacheKey)
        if (cached) return cached.result
        return { success: false, data: [] }
      }
      const cached = announcementCache.get(cacheKey)
      if (cached) return cached.result
      return { success: false, data: [] }
    })
    .finally(() => {
      announcementInFlight.delete(cacheKey)
    })

  announcementInFlight.set(cacheKey, request)
  return request
}

export function invalidateAnnouncementCache(
  tenantId: string,
  target: string,
  username?: string | null
): void {
  announcementCache.delete(buildAnnouncementKey(tenantId, target, username))
  announcementRateLimitedUntil.delete(buildAnnouncementKey(tenantId, target, username))
}
